import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getTenantDepartmentData,
  putTenantDepartmentsData,
} from "@services/API/Requests/TenantDepartmentRequests";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DEPARTMENT } from "@types/department";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { error_message } from "@constants/Errors";
import { KAKELY_PATHS } from "@constants/NavigationPaths";

export const useEditDepartmentGroup = () => {
  const { departmentId } = useParams<{ departmentId: string }>();
  const navigate = useNavigate();

  const [department, setDepartment] = useState<DEPARTMENT | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const [warningModalOpen, setWarningModalOpen] = useState(false);

  const errorToast = (response) => toast.error(`${response}`);
  const successToast = (response) => toast.success(`${response}`);

  const updateErrorToast = (errorMsg: string) => toast.error(`${errorMsg}`);

  const [errors, setErrors] = useState({
    name: false,
    name_kana: false,
    parent_code: false,
    display_order: false,
  });

  useEffect(() => {
    const fetchDepartmentData = async () => {
      try {
        setLoading(false);
        await getTenantDepartmentData(
          setDepartment,
          setLoading,
          setError,
          departmentId,
        );
      } catch (err) {
        setError(error_message.departments.get);
        updateErrorToast(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDepartmentData();
  }, [departmentId]);

  const verifyFields = () => {
    return !Object.values(errors).some((error) => error === true);
  };

  const updateError = (field, value) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: value,
    }));
  };

  const handleInputChange = (key: string, value: string) => {
    setDepartment((prevState) =>
      prevState ? { ...prevState, [key]: value } : prevState,
    );
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const fields: boolean = verifyFields();

    if (!fields) {
      errorToast(error_message.generic.bad_field);
      return;
    }

    if (department) {
      let response;
      setLoading(true);
      await putTenantDepartmentsData(department, setLoading, setError)
        .then((response) => {
          if (response.status == 200) {
            successToast(response.message);
          } else {
            const errorMessage = TranslateErrorMessage(response.message);
            console.error(errorMessage);
            setError(errorMessage);
            errorToast(errorMessage);
          }
        })
        .catch((error) => {
          const errorMessage = TranslateErrorMessage(response.message);
          console.error(errorMessage);
          setError(errorMessage);
          errorToast(errorMessage);
        });
    }
    setLoading(false);
  };

  const handleOpenWarningModal = () => {
    setWarningModalOpen(true);
  };

  const handleCloseWarningModal = () => {
    setWarningModalOpen(false);
  };

  const onCancel = () => {
    navigate(KAKELY_PATHS.allDepartmentSettings);
  };
  return {
    department,
    loading,
    error,
    errors,
    warningModalOpen,
    handleSave,
    updateError,
    handleInputChange,
    handleOpenWarningModal,
    handleCloseWarningModal,
    onCancel,
  };
};
